import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import DotCarousel from '../components/DotCarousel';
import { GlsCard } from '../components/GlsCard';
import { ProjectCard } from './Dashboard';
import PageSearchBar from '../components/PageSearchBar';
import SearchResultsDropdown from '../components/SearchResultsDropdown';
import { PaginatedPosterGrid } from '../components/PaginatedPosterGrid';
import { PosterCard, type PosterItem } from '../components/PosterCard';
import ProjectFiltersDialog from '../components/ProjectFiltersDialog';
import ProjectNavButtons from '../components/ProjectNavButtons';
import { FilePreviewDialog } from '../components/FilePreviewDialog';
import { useAppliedFilterChips } from '../hooks/useAppliedFilterChips';
import {
  projectEssentialApi,
  type ProjectFilters,
  type UniversalSearchGroup,
  type UniversalSearchItem,
} from '../services/api';
import {
  buildPeSearchPageHref,
  countPeFilters,
  hasPeProjectFilter,
  parsePeSearchState,
  peUpcomingGlsHeading,
  stashPePendingPreview,
  type PePendingPreview,
} from '../utils/searchState';
import {
  findSearchGroup,
  groupCount,
  mergeSearchGroups,
  searchItemToGls,
  searchItemToPoster,
  searchItemToProject,
} from '../utils/searchResultsMappers';
import {
  logEbookDownloaded,
  logEbookPreviewed,
  logEdmDownloaded,
  logEdmPreviewed,
  logProjectDetailsPageClicked,
} from '../services/analytics';

const GLS_SLIDE_FLEX = {
  xs: '0 0 50%',
  sm: '0 0 40%',
  md: '0 0 32%',
  lg: '0 0 32%',
};

const PROJECT_SLIDE_FLEX = {
  xs: '0 0 70%',
  sm: '0 0 42%',
  md: '0 0 32%',
  lg: '0 0 24%',
};

const EBOOK_SLIDE_FLEX = {
  xs: '0 0 52%',
  sm: '0 0 36%',
  md: '0 0 26%',
  lg: '0 0 20%',
};

type SectionId = 'gls' | 'projects' | 'ebooks' | 'edms' | 'reports';

interface PreviewTarget {
  id: string;
  kind: UniversalSearchItem['kind'];
  title: string;
  url: string;
  urlPublic: string | null;
  filename: string;
}

function SectionAccordion({
  title,
  count,
  open,
  onToggle,
  children,
}: {
  title: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <Box>
      <Box
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: open ? 1.25 : 0,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover .section-title': { color: 'var(--color-accent-strong)' },
        }}
      >
        <Typography
          className="section-title"
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-strong)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: { xs: '0.95rem', sm: '1.05rem' },
            transition: 'color 120ms ease',
          }}
        >
          {title}
          {typeof count === 'number' && count > 0 ? ` (${count})` : ''}
        </Typography>
        <ExpandMoreIcon
          sx={{
            color: 'var(--color-text-muted)',
            transition: 'transform 200ms ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Box>
      <Collapse in={open} timeout={200} unmountOnExit>
        {children}
      </Collapse>
    </Box>
  );
}

export default function SearchResultsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const searchState = useMemo(
    () => parsePeSearchState(location.search),
    [location.search],
  );
  const { query, filters } = searchState;
  const filterCount = countPeFilters(filters);
  const hasProjectFilter = hasPeProjectFilter(filters);
  const hasCriteria = Boolean(query) || filterCount > 0;

  const [draft, setDraft] = useState(query);
  useEffect(() => setDraft(query), [query]);
  const [dropdownDismissKey, setDropdownDismissKey] = useState(0);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [groups, setGroups] = useState<UniversalSearchGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const [openSections, setOpenSections] = useState<Record<SectionId, boolean>>({
    gls: true,
    projects: true,
    ebooks: true,
    edms: true,
    reports: true,
  });
  const fetchIdRef = useRef(0);

  const toggleSection = (id: SectionId) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const runSearch = useCallback(
    (nextQuery: string, nextFilters: ProjectFilters) => {
      navigate(buildPeSearchPageHref(nextQuery, nextFilters), { replace: true });
    },
    [navigate],
  );

  const setFilters = useCallback(
    (next: ProjectFilters | ((prev: ProjectFilters) => ProjectFilters)) => {
      const resolved = typeof next === 'function' ? next(filters) : next;
      runSearch(query, resolved);
    },
    [filters, query, runSearch],
  );

  const { chips: appliedFilterChips, clearAll: clearAllFilters } = useAppliedFilterChips(
    filters,
    setFilters,
  );

  const handleDropdownSelect = (
    href: string,
    item: UniversalSearchItem | null,
    pendingPreview?: PePendingPreview | null,
  ) => {
    setDropdownDismissKey((k) => k + 1);
    if (item?.kind === 'project') logProjectDetailsPageClicked(item.id);
    if (pendingPreview) stashPePendingPreview(pendingPreview);
    navigate(href, {
      state: {
        pePreviewAt: Date.now(),
        pePendingPreview: pendingPreview ?? null,
      },
    });
  };

  useEffect(() => {
    if (!hasCriteria) {
      setGroups([]);
      setLoading(false);
      setError(null);
      return;
    }
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    projectEssentialApi
      .searchContent(query, filters)
      .then((res) => {
        if (fetchId !== fetchIdRef.current) return;
        setGroups(Array.isArray(res.groups) ? res.groups : []);
        setOpenSections({
          gls: true,
          projects: true,
          ebooks: true,
          edms: true,
          reports: true,
        });
      })
      .catch((err: unknown) => {
        if (fetchId !== fetchIdRef.current) return;
        setError((err as Error)?.message || 'Search failed');
        setGroups([]);
      })
      .finally(() => {
        if (fetchId === fetchIdRef.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- location.search is the key
  }, [location.search, hasCriteria]);

  const glsGroup = findSearchGroup(groups, 'upcoming-gls');
  const projectsGroup = findSearchGroup(groups, 'projects');
  const edmsGroup = findSearchGroup(groups, 'edms');
  const ebookItems = useMemo(
    () => mergeSearchGroups(groups, ['client-engagement-ebooks', 'periodic-ebooks']),
    [groups],
  );
  const reportItems = useMemo(
    () => mergeSearchGroups(groups, ['insights-reports', 'analytics-files']),
    [groups],
  );

  const ebookTotal = useMemo(() => {
    const ids = ['client-engagement-ebooks', 'periodic-ebooks'] as const;
    return ids.reduce((acc, id) => {
      const group = findSearchGroup(groups, id);
      return acc + (group ? groupCount(group) : 0);
    }, 0);
  }, [groups]);

  const reportTotal = useMemo(() => {
    const ids = ['insights-reports', 'analytics-files'] as const;
    return ids.reduce((acc, id) => {
      const group = findSearchGroup(groups, id);
      return acc + (group ? groupCount(group) : 0);
    }, 0);
  }, [groups]);

  const totalCount = useMemo(
    () =>
      (!hasProjectFilter && glsGroup ? groupCount(glsGroup) : 0) +
      (projectsGroup ? groupCount(projectsGroup) : 0) +
      ebookTotal +
      (edmsGroup ? groupCount(edmsGroup) : 0) +
      reportTotal,
    [glsGroup, projectsGroup, ebookTotal, edmsGroup, reportTotal, hasProjectFilter],
  );

  const openPreview = (item: UniversalSearchItem) => {
    void projectEssentialApi
      .recordRecentSearch({
        itemType: item.kind,
        itemId: String(item.id),
        title: item.title,
        thumbnail: item.thumbnail,
        href: item.href,
      })
      .catch(() => {
        /* non-blocking */
      });
    const url = (item.url || '').trim();
    if (!url || url === '#') {
      navigate(item.href);
      return;
    }
    if (item.kind === 'edm') logEdmPreviewed(item.id);
    else logEbookPreviewed(item.id);
    setPreview({
      id: item.id,
      kind: item.kind,
      title: item.title,
      url,
      urlPublic: item.urlPublic ?? null,
      filename: item.filename || `${item.title || item.id}.pdf`,
    });
  };

  const handlePosterPreview = (
    poster: PosterItem,
    kind: UniversalSearchItem['kind'],
    source?: UniversalSearchItem,
  ) => {
    void projectEssentialApi
      .recordRecentSearch({
        itemType: kind,
        itemId: String(poster.id),
        title: poster.title,
        thumbnail: source?.thumbnail ?? poster.cover,
        href: source?.href,
      })
      .catch(() => {
        /* non-blocking */
      });
    if (kind === 'edm') logEdmPreviewed(poster.id);
    else logEbookPreviewed(poster.id);
    setPreview({
      id: poster.id,
      kind,
      title: poster.title,
      url: poster.url,
      urlPublic: poster.urlPublic ?? null,
      filename: poster.filename || `${poster.title || poster.id}.pdf`,
    });
  };

  const summary = (() => {
    if (!hasCriteria) return null;
    if (loading && totalCount === 0) return 'Searching…';
    const noun = totalCount === 1 ? 'result' : 'results';
    if (query && filterCount > 0) {
      return `${totalCount} ${noun} for "${query}" with ${filterCount} filter${
        filterCount === 1 ? '' : 's'
      }`;
    }
    if (query) return `${totalCount} ${noun} for "${query}"`;
    return `${totalCount} ${noun} for the selected filters`;
  })();

  const resetKey = `${query}|${JSON.stringify(filters)}`;

  return (
    <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ minWidth: 0 }}>
      <Box>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 999,
            borderColor: 'var(--color-border-strong)',
            color: 'var(--color-text-strong)',
            bgcolor: 'var(--color-surface)',
            '&:hover': {
              borderColor: 'var(--color-accent)',
              bgcolor: 'var(--color-accent-soft)',
            },
          }}
        >
          Back
        </Button>
      </Box>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: 'var(--color-accent)',
          textAlign: 'center',
          letterSpacing: '0.01em',
          fontSize: { xs: '1.6rem', sm: '2rem', md: '2.25rem' },
        }}
      >
        Search Results
      </Typography>

      <PageSearchBar
        value={draft}
        onChange={setDraft}
        onSubmit={(next) => runSearch(next, filters)}
        onClearQuery={() => runSearch('', filters)}
        placeholder="Search projects, EDMs, eBooks and reports…"
        loading={loading}
        filterCount={filterCount}
        onOpenFilters={() => setFiltersOpen(true)}
        appliedFilters={appliedFilterChips}
        onClearAllFilters={clearAllFilters}
        dropdownContent={
          <SearchResultsDropdown
            query={draft.trim()}
            filters={filters}
            onSelect={handleDropdownSelect}
          />
        }
        dismissDropdownKey={dropdownDismissKey}
      />

      <ProjectNavButtons />

      {summary && (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: 'var(--color-text-strong)' }}
        >
          {summary}
        </Typography>
      )}

      {!hasCriteria ? (
        <EmptyState
          icon={<TravelExploreIcon sx={{ fontSize: 40 }} />}
          title="Search all of Project Essentials"
          body="Type a project name, EDM title or report above and press Enter. Every match — projects, upcoming GLS sites, EDMs, eBooks and reports — lands on this page."
        />
      ) : error ? (
        <EmptyState
          icon={<SearchOffIcon sx={{ fontSize: 40 }} />}
          title="Search failed"
          body={error}
        />
      ) : loading && totalCount === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : totalCount === 0 ? (
        <EmptyState
          icon={<SearchOffIcon sx={{ fontSize: 40 }} />}
          title={query ? `No matches for "${query}"` : 'No matches'}
          body={
            filterCount > 0
              ? 'Try removing a filter or searching for a shorter phrase.'
              : 'Try a shorter phrase, or check the spelling of the project name.'
          }
        />
      ) : (
        <Stack spacing={{ xs: 2.5, sm: 3 }}>
          {!hasProjectFilter && glsGroup && glsGroup.items.length > 0 && (
            <SectionAccordion
              title={peUpcomingGlsHeading(filters)}
              count={groupCount(glsGroup)}
              open={openSections.gls}
              onToggle={() => toggleSection('gls')}
            >
              <DotCarousel
                slides={glsGroup.items.map((item) => ({
                  key: item.id,
                  node: <GlsCard item={searchItemToGls(item)} />,
                }))}
                slideFlex={GLS_SLIDE_FLEX}
                gap={{ xs: 0.25, sm: 1.25 }}
              />
            </SectionAccordion>
          )}

          {projectsGroup && projectsGroup.items.length > 0 && (
            <SectionAccordion
              title="Projects"
              count={groupCount(projectsGroup)}
              open={openSections.projects}
              onToggle={() => toggleSection('projects')}
            >
              <DotCarousel
                slides={projectsGroup.items.map((item) => ({
                  key: item.id,
                  node: <ProjectCard project={searchItemToProject(item)} />,
                }))}
                slideFlex={PROJECT_SLIDE_FLEX}
                gap={{ xs: 0.5, sm: 1.25 }}
              />
            </SectionAccordion>
          )}

          {ebookItems.length > 0 && (
            <SectionAccordion
              title="eBooks"
              count={ebookTotal}
              open={openSections.ebooks}
              onToggle={() => toggleSection('ebooks')}
            >
              <DotCarousel
                slides={ebookItems.map((item) => {
                  const poster = searchItemToPoster(item);
                  return {
                    key: `${item.id}:${item.kind}`,
                    node: (
                      <PosterCard
                        item={poster}
                        onPreview={() => handlePosterPreview(poster, item.kind, item)}
                        onDownloadClick={() => logEbookDownloaded(item.id)}
                        shareKind="ebook"
                      />
                    ),
                  };
                })}
                slideFlex={EBOOK_SLIDE_FLEX}
                gap={{ xs: 0.5, sm: 1.25 }}
              />
            </SectionAccordion>
          )}

          {edmsGroup && edmsGroup.items.length > 0 && (
            <SectionAccordion
              title="EDMs"
              count={groupCount(edmsGroup)}
              open={openSections.edms}
              onToggle={() => toggleSection('edms')}
            >
              <PaginatedPosterGrid
                items={edmsGroup.items.map(searchItemToPoster)}
                resetKey={resetKey}
                onPreview={(poster) => {
                  const item = edmsGroup.items.find((row) => row.id === poster.id);
                  handlePosterPreview(poster, 'edm', item);
                }}
                onDownloadClick={(poster) => logEdmDownloaded(poster.id)}
                shareKind="edm"
              />
            </SectionAccordion>
          )}

          {reportItems.length > 0 && (
            <SectionAccordion
              title="Reports"
              count={reportTotal}
              open={openSections.reports}
              onToggle={() => toggleSection('reports')}
            >
              <PaginatedPosterGrid
                items={reportItems.map(searchItemToPoster)}
                resetKey={`${resetKey}:reports`}
                onPreview={(poster) => {
                  const item = reportItems.find((row) => row.id === poster.id);
                  if (item) openPreview(item);
                }}
                onDownloadClick={() => {}}
                shareKind="report"
              />
            </SectionAccordion>
          )}
        </Stack>
      )}

      <ProjectFiltersDialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onApply={(next) => runSearch(query, next)}
      />

      <FilePreviewDialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        title={preview?.title ?? ''}
        url={preview?.url ?? null}
        urlPublic={preview?.urlPublic ?? null}
        filename={preview?.filename ?? ''}
        onDownloadClick={() => {
          if (!preview) return;
          if (preview.kind === 'edm') logEdmDownloaded(preview.id);
          else logEbookDownloaded(preview.id);
        }}
        forceBlobDownload
      />
    </Stack>
  );
}

function EmptyState({
  icon,
  title,
  body,
}: {
  icon: ReactNode;
  title: string;
  body: string;
}) {
  return (
    <Stack
      alignItems="center"
      spacing={1}
      sx={{
        py: { xs: 5, sm: 7 },
        px: 2,
        textAlign: 'center',
        color: 'var(--color-text-muted)',
        border: '1px dashed var(--color-border)',
        borderRadius: 2,
      }}
    >
      {icon}
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}
      >
        {title}
      </Typography>
      <Typography variant="body2" sx={{ maxWidth: 520 }}>
        {body}
      </Typography>
    </Stack>
  );
}
